import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

// Site-wide default building. Shown on first load and whenever the operator
// hasn't explicitly picked one yet. Buildings are stored lowercase in the DB
// (e.g. "pavilion", "pool", "farm").
export const DEFAULT_BUILDING_ID = 'pavilion';

// One shared, persisted building selection for the whole app. Using a single
// atom (instead of a per-page atom) is what makes the chosen building survive
// navigation between pages, and atomWithStorage makes it survive reloads too.
// Empty string means "not chosen yet" so callers fall back to the resolved
// default below.
// getOnInit: read localStorage on the very first render, so pages (and effects that
// write the selection back) never see a transient '' before the stored value loads.
export const selectedBuildingIdAtom = atomWithStorage<string>('fpm.selectedBuildingId', '', undefined, {
  getOnInit: true,
});

/**
 * Building to default to when the operator hasn't picked one yet: prefer the
 * site default (pavilion, matched case-insensitively) when it exists, otherwise
 * the first building alphabetically. Returns null when no buildings are known.
 */
export function resolveDefaultBuildingId(buildingIds: string[]): string | null {
  if (!buildingIds.length) return null;
  const preferred = buildingIds.find((id) => id.toLowerCase() === DEFAULT_BUILDING_ID);
  if (preferred) return preferred;
  return [...buildingIds].sort((a, b) => a.localeCompare(b))[0];
}

// The dashboards follow the same shared building selection as every other page.
// "All buildings" is a dashboard-only overview on top of it: it is remembered as
// the building that was selected when "All" was chosen, so it stays "All" while
// you move between dashboards, but as soon as a different building is picked on
// any page the dashboard follows that building again. null = not in "All" mode.
export const dashboardAllForBuildingAtom = atom<string | null>(null);
